'use client';

import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'next/navigation';
import { useCityStore } from '@/store/useCityStore';
import FilterPanel from './FilterPanel';

/**
 * MobileListingTopBar
 * Sticky top bar shown on listing pages (buy/rent/plots/projects/civil) on mobile.
 * Contains: City selector | Locality multi-select | Filter icon
 * Below bar: selected locality chips
 *
 * Props:
 *   filters        - current filter object
 *   onFilterChange - callback(newFilters)
 *   listingType    - 'buy' | 'rent' | 'plots' | 'projects' | 'civil'
 *   onSearch       - callback() called when city or locality changes (triggers page refetch)
 */
export default function MobileListingTopBar({ filters, onFilterChange, listingType, onSearch }) {
    const [showCityModal, setShowCityModal] = useState(false);
    const [showLocalityModal, setShowLocalityModal] = useState(false);
    const [showFilterDrawer, setShowFilterDrawer] = useState(false);

    const searchParams = useSearchParams();
    const cityId = searchParams.get('cityId');

    const {
        cities,
        localities,
        selectedCity,
        selectedLocalities,
        citySearch,
        localitySearch,
        setCitySearch,
        setLocalitySearch,
        setSelectedCity,
        setSelectedLocalities,
        addLocality,
        removeLocality,
        fetchLocalities,
        fetchCities,
    } = useCityStore();

    // Sync city and locality selections from URL params
    useEffect(() => {
        const localitiesParam = searchParams.get('localities');
        fetchCities(cityId, localitiesParam);
    }, [cityId, searchParams]);

    // Count active filters (excluding localities which are shown in the bar)
    const activeFilterCount = Object.keys(filters || {}).reduce((count, key) => {
        const val = filters[key];
        if (Array.isArray(val)) return count + (val.length > 0 ? 1 : 0);
        if (val !== undefined && val !== null && val !== '') return count + 1;
        return count;
    }, 0);

    // Local search for city modal
    const [cityModalSearch, setCityModalSearch] = useState('');
    // Local search for locality modal
    const [localityModalSearch, setLocalityModalSearch] = useState('');

    const filteredModalCities = cities.filter(c =>
        c.name.toLowerCase().includes(cityModalSearch.toLowerCase())
    );

    const filteredModalLocalities = localities.filter(l => {
        const matchSearch = (l?.name || '').toLowerCase().includes(localityModalSearch.toLowerCase());
        return matchSearch;
    });

    const handleCitySelect = (city) => {
        setCitySearch(city.name);
        setSelectedCity(city);
        setSelectedLocalities([]);
        setLocalityModalSearch('');
        fetchLocalities(city.id);
        setShowCityModal(false);
        setCityModalSearch('');
        if (onSearch) onSearch(city, []);
    };

    const handleLocalityToggle = (locality) => {
        const isSelected = selectedLocalities.some(l => l.id === locality.id);
        if (isSelected) {
            removeLocality(locality.id);
        } else {
            addLocality(locality);
        }
    };

    const handleLocalityDone = () => {
        setShowLocalityModal(false);
        setLocalityModalSearch('');
        if (onSearch) onSearch(selectedCity, selectedLocalities);
    };

    const localityCount = selectedLocalities.length;

    return (
        <>
            {/* ─── Sticky Top Bar ─── */}
            <div className="lg:hidden sticky top-14 z-40 bg-white border-b border-gray-200 shadow-sm">
                <div className="flex items-stretch h-12">
                    {/* City Selector */}
                    <button
                        id="mobile-city-selector"
                        onClick={() => setShowCityModal(true)}
                        className="flex items-center gap-1.5 px-3 border-r border-gray-200 min-w-0 shrink-0 max-w-[38%]"
                        aria-label="Select city"
                    >
                        <svg className="w-4 h-4 text-primary shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <div className="flex flex-col items-start min-w-0 flex-1">
                            <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest leading-none">CITY</span>
                            <span className="text-xs font-bold text-gray-900 truncate leading-tight">{selectedCity?.name || 'Select City'}</span>
                        </div>
                        <svg className="w-3 h-3 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>

                    {/* Locality Selector */}
                    <button
                        id="mobile-locality-selector"
                        onClick={() => setShowLocalityModal(true)}
                        className="flex items-center gap-1.5 px-3 flex-1 min-w-0 border-r border-gray-200"
                        aria-label="Select localities"
                    >
                        {/* Location pin icon with navigation effect */}
                        <svg className="w-4 h-4 text-primary shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <circle cx="12" cy="12" r="3" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
                        </svg>
                        <div className="flex flex-col items-start min-w-0 flex-1">
                            <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest leading-none">LOCALITY</span>
                            <span className="text-xs font-bold text-gray-900 truncate leading-tight">
                                {localityCount > 0 ? `${localityCount} Selected` : 'All Localities'}
                            </span>
                        </div>
                        <svg className="w-3 h-3 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                        </svg>
                    </button>

                    {/* Filter Icon */}
                    <button
                        id="mobile-filter-button"
                        onClick={() => setShowFilterDrawer(true)}
                        className="flex items-center justify-center gap-1 px-4 shrink-0 relative"
                        aria-label="Open filters"
                    >
                        <svg className="w-5 h-5 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                        </svg>
                        {activeFilterCount > 0 && (
                            <span className="absolute top-2 right-2 w-4 h-4 bg-primary text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                                {activeFilterCount}
                            </span>
                        )}
                    </button>
                </div>

                {/* Selected Locality Chips */}
                {selectedLocalities.length > 0 && (
                    <div className="flex items-center gap-2 px-3 py-2 overflow-x-auto scrollbar-hide border-t border-gray-100 bg-gray-50/60">
                        {selectedLocalities.map((loc) => (
                            <span
                                key={loc.id}
                                className="flex items-center gap-1 bg-primary/10 text-primary text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap shrink-0"
                            >
                                {loc.name}
                                <button
                                    onClick={() => { removeLocality(loc.id); if (onSearch) onSearch(selectedCity, selectedLocalities.filter(l => l.id !== loc.id)); }}
                                    className="ml-0.5 hover:text-primary-dark transition-colors"
                                    aria-label={`Remove ${loc.name}`}
                                >
                                    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </span>
                        ))}
                    </div>
                )}
            </div>

            {/* ─── City Modal ─── */}
            {showCityModal && (
                <div className="fixed inset-0 z-[150] lg:hidden flex flex-col bg-white">
                    {/* Header */}
                    <div className="flex items-center gap-3 px-4 py-3 bg-primary text-white shrink-0">
                        <button
                            onClick={() => { setShowCityModal(false); setCityModalSearch(''); }}
                            className="p-1"
                            aria-label="Close city selector"
                        >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 12H5m7-7l-7 7 7 7" />
                            </svg>
                        </button>
                        <span className="font-bold text-base">Select City</span>
                    </div>

                    {/* Search */}
                    <div className="px-4 py-3 border-b border-gray-200 shrink-0">
                        <div className="flex items-center gap-2 bg-gray-100 rounded-xl px-3 py-2">
                            <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            <input
                                autoFocus
                                type="text"
                                placeholder="Search city..."
                                value={cityModalSearch}
                                onChange={(e) => setCityModalSearch(e.target.value)}
                                className="flex-1 bg-transparent text-sm text-gray-800 placeholder-gray-400 outline-none"
                            />
                            {cityModalSearch && (
                                <button onClick={() => setCityModalSearch('')} className="text-gray-400">
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            )}
                        </div>
                    </div>

                    {/* City List */}
                    <div className="flex-1 overflow-y-auto">
                        {filteredModalCities.length === 0 ? (
                            <p className="text-center text-gray-400 py-10 text-sm">No cities found</p>
                        ) : (
                            filteredModalCities.map((city) => {
                                const isSelected = selectedCity?.id === city.id;
                                return (
                                    <button
                                        key={city.id}
                                        onClick={() => handleCitySelect(city)}
                                        className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-left border-b border-gray-100 transition-colors
                                            ${isSelected ? 'bg-primary/8 text-primary' : 'text-gray-800 hover:bg-gray-50'}`}
                                    >
                                        <svg className={`w-3.5 h-3.5 shrink-0 ${isSelected ? 'text-primary' : 'text-gray-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                        <span className={`font-medium text-xs ${isSelected ? 'font-bold text-primary' : ''}`}>{city.name}</span>
                                        {isSelected && (
                                            <svg className="w-3.5 h-3.5 text-primary ml-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
                                            </svg>
                                        )}
                                    </button>
                                );
                            })
                        )}
                    </div>
                </div>
            )}

            {/* ─── Locality Modal (Multi-select with checkboxes) ─── */}
            {showLocalityModal && (
                <div className="fixed inset-0 z-[150] lg:hidden flex flex-col bg-white">
                    {/* Header */}
                    <div className="flex items-center gap-3 px-4 py-3 bg-primary text-white shrink-0">
                        <button
                            onClick={() => { setShowLocalityModal(false); setLocalityModalSearch(''); }}
                            className="p-1"
                            aria-label="Close locality selector"
                        >
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 12H5m7-7l-7 7 7 7" />
                            </svg>
                        </button>
                        <span className="font-bold text-base flex-1">Select Locality</span>
                        {localityCount > 0 && (
                            <span className="bg-white/20 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                                {localityCount} selected
                            </span>
                        )}
                    </div>

                    {/* Search */}
                    <div className="px-4 py-3 border-b border-gray-200 shrink-0">
                        <div className="flex items-center gap-2 bg-gray-100 rounded-xl px-3 py-2">
                            <svg className="w-4 h-4 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                            <input
                                autoFocus
                                type="text"
                                placeholder="Search locality..."
                                value={localityModalSearch}
                                onChange={(e) => setLocalityModalSearch(e.target.value)}
                                className="flex-1 bg-transparent text-sm text-gray-800 placeholder-gray-400 outline-none"
                            />
                            {localityModalSearch && (
                                <button onClick={() => setLocalityModalSearch('')} className="text-gray-400">
                                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Locality Checkbox List */}
                    <div className="flex-1 overflow-y-auto">
                        {filteredModalLocalities.length === 0 ? (
                            <p className="text-center text-gray-400 py-10 text-sm">No localities found</p>
                        ) : (
                            filteredModalLocalities.map((loc) => {
                                const isChecked = selectedLocalities.some(l => l.id === loc.id);
                                return (
                                    <label
                                        key={loc.id}
                                        className={`flex items-center gap-2.5 px-4 py-2.5 border-b border-gray-100 cursor-pointer transition-colors
                                            ${isChecked ? 'bg-primary/5' : 'hover:bg-gray-50'}`}
                                    >
                                        {/* Custom Checkbox */}
                                        <span className={`w-4 h-4 rounded border-2 flex items-center justify-center shrink-0 transition-colors
                                            ${isChecked ? 'bg-primary border-primary' : 'border-gray-300 bg-white'}`}>
                                            {isChecked && (
                                                <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                                </svg>
                                            )}
                                        </span>
                                        <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={() => handleLocalityToggle(loc)}
                                            className="sr-only"
                                        />
                                        <span className={`text-xs font-medium flex-1 ${isChecked ? 'text-primary font-semibold' : 'text-gray-800'}`}>
                                            {loc.name}
                                        </span>
                                    </label>
                                );
                            })
                        )}
                    </div>

                    {/* Done Button */}
                    <div className="shrink-0 p-4 border-t border-gray-200 bg-white">
                        {localityCount > 0 && (
                            <div className="flex flex-wrap gap-2 mb-3">
                                {selectedLocalities.map((loc) => (
                                    <span
                                        key={loc.id}
                                        className="flex items-center gap-1 bg-primary/10 text-primary text-xs font-semibold px-2.5 py-1 rounded-full"
                                    >
                                        {loc.name}
                                        <button
                                            onClick={(e) => { e.stopPropagation(); removeLocality(loc.id); }}
                                            className="hover:text-primary-dark"
                                        >
                                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                        <button
                            onClick={handleLocalityDone}
                            className="w-full bg-primary text-white font-bold py-3 rounded-xl text-sm hover:bg-primary-dark transition-colors"
                        >
                            {localityCount > 0 ? `Apply ${localityCount} Localities` : 'Done'}
                        </button>
                    </div>
                </div>
            )}

            {/* ─── Filter Drawer (right slide-in) ─── */}
            {showFilterDrawer && (
                <div className="fixed inset-0 z-[150] lg:hidden">
                    <div
                        className="absolute inset-0 bg-black/30 backdrop-blur-sm"
                        onClick={() => setShowFilterDrawer(false)}
                        aria-hidden="true"
                    />
                    <div className="absolute right-0 top-0 bottom-0 w-[80%] bg-white overflow-y-auto shadow-2xl z-10">
                        <FilterPanel
                            filters={filters}
                            onChange={(f) => { onFilterChange(f); }}
                            listingType={listingType}
                            isMobile={true}
                            onClose={() => setShowFilterDrawer(false)}
                        />
                    </div>
                </div>
            )}
        </>
    );
}
